import * as React from 'react';
import memoize from 'lodash/memoize';
import toNumber from 'lodash/toNumber';
import upperCase from 'lodash/upperCase';
import get from 'lodash/get';
import defaultTo from 'lodash/defaultTo';
import sumBy from 'lodash/sumBy';
import { Adjustment, BillingDoc, Refund, RefundItem, TaxItems } from '@atlassian/cssxp-types-billing';
import {
  DOMAIN_PATH,
  INVOICE_PATH_2,
  NEW_DATE_TIME_FORMAT,
  ORGANIZATION_PATH,
  REFUND_PATH_2,
  SourceSystem,
} from '@atlassian/cssxp-typescommon';
import { AdaRedirectButton, BreadcrumbItem, FormattedDate, RefundedLozenge, SummarySectionV2 } from '@css-xp/base';
//@ts-ignore
import RefundIcon from '@css-xp/base/assets/img/icon-refund.svg';
import { Organization } from '@atlassian/cssxp-types-organization';
import { isInvoiceRefunded } from '@atlassian/cssxp-utils-invoice';
// @ts-ignore
import AtlassianPartnerIcon from '@css-xp/base/assets/img/icon-atlassian-partner.svg';
import { PARTNER_MARGIN_TYPE } from '@css-xp/ppc-constants';
import { InvoiceMargins } from '@css-xp/graphtypes/src/generated-types-new';
import { InvoiceRefundStatusLozenge } from '../../invoices';
import { InvoiceRefundCreditNote } from '../../invoices/src/components/invoice-refund-creditnote-lozenge';

export const getViewInAdaRefundUrl = memoize(
  (baseUrl?: string, id?: string) => {
    if (id && baseUrl) {
      const idNumber = id.startsWith('RF-') ? id.slice(3) : id;
      return `${baseUrl}/ViewRefund.jspa?refundId=${encodeURIComponent(idNumber)}`;
    }
    return '';
  },
  (...arg) => arg[1]
);

export const getRefundSubheader = (refund?: Refund, shouldHidePartnerLogo?: boolean): React.ReactNode => {
  return (
    <div className="invoice-status">
      {!shouldHidePartnerLogo && refund?.isPartner && (
        <span data-testid="partner-logo">
          {/*// @ts-ignore */}
          <img src={AtlassianPartnerIcon} height={'15px'} alt="Atlassian partner icon" />
        </span>
      )}
      {refund && isInvoiceRefunded(refund) ? <RefundedLozenge /> : <InvoiceRefundStatusLozenge status="PENDING" />}
      {refund?.type !== 'full-item-refund' && (
        <>
          {refund?.type && (
            <span className="pl-8 refund-credit-note-status">
              {<InvoiceRefundCreditNote credittype={refund.type} />}
            </span>
          )}
        </>
      )}
    </div>
  );
};

export const getRefundIcon = (): JSX.Element => {
  // @ts-ignore
  return <img src={RefundIcon} height={'60px'} alt="invoice" />;
};

export const getRefundActions = (adaBaseUrl: string, billingDoc: BillingDoc) => {
  const { id } = billingDoc;
  return (
    <>
      {billingDoc?.sourceSystem === SourceSystem.HAMS && (
        <AdaRedirectButton href={getViewInAdaRefundUrl(adaBaseUrl, id)} />
      )}
    </>
  );
};

export const getRefundAdditionalDetail = (refund?: Refund) => {
  if (!refund) {
    return null;
  }

  return (
    <>
      <SummarySectionV2.SummaryGridCalloutItem styles={{ gridRow: '1' }}>
        <div className="summary-item">
          <h4 className="summary-item-title">Issued on</h4>
          {refund.createdDate ? (
            <FormattedDate date={refund.createdDate} showTime={true} format={NEW_DATE_TIME_FORMAT} />
          ) : (
            'Not available'
          )}
        </div>
      </SummarySectionV2.SummaryGridCalloutItem>

      <SummarySectionV2.SummaryGridCalloutItem styles={{ gridRow: '1' }}>
        {upperCase(refund.sourceSystem ?? '') === SourceSystem.CCP && (
          <div className="summary-item">
            <h4 className="summary-item-title">PO number</h4>
            {refund?.purchaseOrderNumber ? refund?.purchaseOrderNumber : 'Not available'}
          </div>
        )}
      </SummarySectionV2.SummaryGridCalloutItem>

      <SummarySectionV2.SummaryGridCalloutItem styles={{ gridRow: '1' }}>
        <div className="summary-item">
          <h4 className="summary-item-title">Transaction ID</h4>
          {refund.arn ? refund.arn : '-'}
        </div>
      </SummarySectionV2.SummaryGridCalloutItem>
    </>
  );
};

export const getRefundBreadcrumbTrail = (billingDoc: BillingDoc, organization?: Organization) => {
  const { id, orderId, domain } = billingDoc as Refund;
  const breadcrumbTrail: BreadcrumbItem.BreadcrumbItem[] = [];

  breadcrumbTrail.unshift({
    title: id,
    href: `${REFUND_PATH_2}/${encodeURIComponent(id)}`,
  });

  if (orderId) {
    breadcrumbTrail.unshift({
      title: orderId,
      href: `${INVOICE_PATH_2}/${encodeURIComponent(orderId)}`,
    });
  }

  if (domain) {
    breadcrumbTrail.unshift({
      title: domain,
      href: `${DOMAIN_PATH}/${encodeURIComponent(domain)}`,
    });
  }

  if (organization) {
    breadcrumbTrail.unshift({
      title: `${organization.organizationName}`,
      href: `${ORGANIZATION_PATH}/${encodeURIComponent(`${organization.organizationId}`)}`,
    });
  }

  return breadcrumbTrail;
};

export const adjustedAmount = (
  subtotal: number | string | undefined | null,
  discount: Adjustment[] | null | undefined,
  partnerMarginAmount: number | null
): number => {
  const actualSubtotal = toNumber(subtotal);
  const actualDiscount = sumBy(discount, 'amount');
  let totalAmount = 0;
  if (!isNaN(actualSubtotal) && actualSubtotal !== 0 && !isNaN(actualDiscount)) {
    totalAmount = actualSubtotal - actualDiscount;
  }
  if (partnerMarginAmount) {
    totalAmount = totalAmount - partnerMarginAmount;
  }
  return totalAmount;
};

export const getTotalPartnerMarginAmount = (margins: InvoiceMargins[] | null) => {
  if (!margins) return 0;
  return margins?.reduce((totalAmount: number, margin: InvoiceMargins) => {
    if (margin?.type === PARTNER_MARGIN_TYPE) {
      return totalAmount + defaultTo(margin?.amount, 0);
    }
    return totalAmount;
  }, 0);
};

export const getRefundDstLineItemDetails = (lineItems: RefundItem[] | null | undefined) => {
  let totalExTax = 0;
  let totalIncTax = 0;
  const dstTaxItems: TaxItems[] = [];
  lineItems?.forEach((item: RefundItem) => {
    const exTax = adjustedAmount(
      item.totalExTax,
      item.adjustments,
      getTotalPartnerMarginAmount(get(item, 'margins', []))
    );
    const incTax = adjustedAmount(item.total, null, null);
    (item?.taxItems || []).forEach((taxItem) => {
      const tax = toNumber(taxItem.tax);
      if (tax && !isNaN(tax)) {
        dstTaxItems.push(taxItem);
      }
    });
    totalExTax += exTax;
    totalIncTax += incTax;
  });

  return { totalExTax, totalIncTax, dstTaxItems };
};
